/**
 * Pantallas fuera del flujo de captura: enrolamiento (encargado), retomar un
 * borrador, la confirmación de guardado, los pendientes en dos voces (operario
 * simple / detalle técnico en config, H10 de la ronda) y la config del
 * encargado (mantener apretado el encabezado — nunca a un toque).
 * v3: sin fotos; «cargados desde este teléfono» por jornada y por persona
 * (visita 5/9, r. 17); «Actualizar listas» a la vista.
 */

import { actualizarPreferencias, type Entidad, type FilaOutbox, type FotoLocal, type Recibo, type Resumen } from '../datos/bd';
import { acusarIncidente, bandeja, cargadosEnLaJornada, contarPendientes, esperasDePadre, pendientesPorEpoch, propiosDeProduccionDesde, propiosDeVerdeDesde, type EsperaDePadre } from '../datos/cola';
import { acusarFotoFallada, bandejaDeFotos, contarFotos, fotosParaSubir, limpiarFallaFoto } from '../datos/fotos';
import { BOLSAS_DE_TOLERANCIA, TOLERANCIA_SOBRE_NETO } from '../nucleo/embolsado';
import { formatearNumero, NOMBRE_LETRA } from '../nucleo/muestra';
import { aceptarReloj, textoDelAviso } from './aviso-reloj';
import { rehacerMuestraRechazada } from './pantallas-calidad';
import { MOTIVOS_DE_ROTULO, QUE_PASO_ROTULO, rehacerAperturaRechazada } from './pantallas-envasado';
import { enrolar } from '../datos/enrolar';
import { cuandoCorto, horaCorta, instanteLocal, MINUTO_MS, NOMBRE_TURNO } from '../nucleo/tiempo';
import { proyectarVerdeReciente, totalesDe } from '../nucleo/verde-reciente';
import { proyectarProduccionReciente, totalesDeProduccion, type FilaProduccionReciente, type MotivoNoSuma, type TipoDeLote } from '../nucleo/produccion-reciente';
import { nombreDeCarroDeDescarte, type BorradorCualquiera } from '../nucleo/viaje';
import { botonMantener, chip, el, flag, icono, kg } from './componentes';
import { acciones, ahoraMs, app, descartarBorrador, esBorradorDeEnvasado, guardarBorrador, irA, listarBorradores, type Vista, recalcularRotulosEnBandeja } from './estado';

export function pintarEnrolar(cuerpo: HTMLElement, vista: Extract<Vista, { tipo: 'enrolar' }>): void {
	cuerpo.appendChild(el('div', { class: 'q arriba' }, 'Enrolar este teléfono'));
	cuerpo.appendChild(
		el('div', { class: 'qsub' }, 'Lo hace el encargado, una sola vez. El código se genera en la oficina y vence a las 24 horas.'),
	);

	const campoCodigo = el('input', {
		class: 'campo',
		autocapitalize: 'characters',
		autocomplete: 'off',
		spellcheck: 'false',
		placeholder: 'CÓDIGO (XXXXX-XXXXX)',
	});
	campoCodigo.oninput = () => {
		// Mayúsculas y guion automáticos: el código se tipea tal cual se dictó.
		const limpio = campoCodigo.value
			.toUpperCase()
			.replace(/[^0-9A-Z]/g, '')
			.slice(0, 10);
		campoCodigo.value = limpio.length > 5 ? `${limpio.slice(0, 5)}-${limpio.slice(5)}` : limpio;
	};
	cuerpo.appendChild(campoCodigo);
	const campoNombre = el('input', { class: 'campo', autocomplete: 'off', placeholder: 'Nombre del teléfono (ej: Báscula 1)' });
	cuerpo.appendChild(campoNombre);
	if (vista.error) cuerpo.appendChild(flag('stop', vista.error, ''));

	const boton = el('button', { class: 'btn go ancho', type: 'button' }, vista.ocupado ? 'Enrolando…' : 'Enrolar');
	if (vista.ocupado) boton.setAttribute('disabled', '');
	boton.onclick = async () => {
		const codigo = campoCodigo.value.trim();
		const nombre = campoNombre.value.trim();
		if (codigo.length < 8 || nombre.length === 0) {
			irA({ tipo: 'enrolar', error: 'Completá el código y el nombre del teléfono.' });
			return;
		}
		irA({ tipo: 'enrolar', ocupado: true });
		const resultado = await enrolar(app.bd, codigo, nombre);
		if (resultado.ok) {
			// Gate «listo sin señal» (A8): antes de declarar el teléfono operativo se
			// espera (con tope) a que el service worker esté activo con el shell
			// precacheado. Si no llega, el arranque avisa en Pendientes.
			if ('serviceWorker' in navigator) {
				await Promise.race([navigator.serviceWorker.ready, new Promise((r) => setTimeout(r, 6000))]).catch(() => null);
			}
			window.location.reload(); // arranque limpio con sesión y catálogos frescos
		} else {
			irA({ tipo: 'enrolar', error: resultado.error });
		}
	};
	cuerpo.appendChild(boton);
}

/** ¿El teléfono puede trabajar sin señal? SW controlando + shell en cache. */
export async function listoSinSenal(): Promise<boolean> {
	if (!('serviceWorker' in navigator) || !('caches' in window)) return false;
	if (!navigator.serviceWorker.controller) return false;
	const nombres = await caches.keys().catch(() => []);
	return nombres.some((n) => n.startsWith('perejil-shell-'));
}

/**
 * Cómo se nombra cada borrador a medias. Un mapa y no un ternario: con seis
 * flujos de producción encima de los dos de verde, «lo que no es viaje es
 * descarte» empezaba a mentir (Codex, ronda del plan, 8/9).
 */
const QUE_QUEDO: Record<BorradorCualquiera['tipo'], { pregunta: string; quedo: string }> = {
	viaje: { pregunta: 'este viaje', quedo: 'un viaje' },
	descarte: { pregunta: 'este descarte', quedo: 'un descarte' },
	abrir: { pregunta: 'la apertura del silo', quedo: 'un silo abriéndose' },
	turno: { pregunta: 'el cambio de turno', quedo: 'un cambio de turno' },
	cerrar: { pregunta: 'el cierre del silo', quedo: 'un cierre de silo' },
	descartar: { pregunta: 'el descarte del silo', quedo: 'un descarte de silo' },
	carro: { pregunta: 'el cambio de carro', quedo: 'un cambio de carro' },
	parada: { pregunta: 'la parada', quedo: 'una parada' },
	// v6 (§12.2, §12.4)
	aviso: { pregunta: 'el aviso para las demás', quedo: 'un aviso' },
	nota: { pregunta: 'la nota al silo', quedo: 'una nota de silo' },
	// Calidad no pasa por acá (su lista muestra todas las hojas), pero el mapa es total.
	muestra: { pregunta: 'la muestra', quedo: 'una hoja de muestra' },
	// v5 · envasado.
	embolsar: { pregunta: 'el embolsado del precinto', quedo: 'un embolsado' },
	cerrar_pallet: { pregunta: 'el cierre del pallet', quedo: 'un cierre de pallet' },
	rehacer_pallet: { pregunta: 'el pallet que volvés a mandar con otro rótulo', quedo: 'un pallet por volver a mandar' },
	anular_pallet: { pregunta: 'el pallet que se tira', quedo: 'un pallet por tirar' },
	// v6 (§12.5, §12.4)
	cambiar_letra: { pregunta: 'el cambio de calidad del precinto', quedo: 'un cambio de calidad' },
	nota_precinto: { pregunta: 'la nota al precinto', quedo: 'una nota de precinto' },
	// §12.18
	corregir_carga: { pregunta: 'la corrección de las bolsas', quedo: 'una corrección de bolsas' },
	anular_carga: { pregunta: 'la carga que se anula', quedo: 'una carga por anular' },
};

export function pintarRetomar(cuerpo: HTMLElement, pie: HTMLElement, vista: Extract<Vista, { tipo: 'retomar' }>): void {
	// Con más de uno a medias se ofrecen de a uno, pero se dice cuántos son y se puede
	// pasar al otro sin borrar nada (revisión del 12/9).
	const cuantos = vista.borradores.length;
	const indice = Math.min(vista.indice ?? 0, cuantos - 1);
	const borrador: BorradorCualquiera = vista.borradores[indice];
	cuerpo.appendChild(el('div', { class: 'q arriba' }, `¿Seguís con ${QUE_QUEDO[borrador.tipo].pregunta}?`));
	cuerpo.appendChild(
		el(
			'div',
			{ class: 'qsub' },
			cuantos > 1
				? `Quedaron ${cuantos} cosas a medias en este teléfono · ${indice + 1} de ${cuantos}.`
				: `Quedó ${QUE_QUEDO[borrador.tipo].quedo} a medias en este teléfono.`,
		),
	);
	const detalle = el('div', { class: 'ticket' });
	const filas: Array<[string, string]> = [['Último toque', horaCorta(borrador.actualizado_en)]];
	if (borrador.tipo === 'viaje') {
		filas.push(['Carros', borrador.carros.length > 0 ? borrador.carros.map((c) => `N° ${c.numero}`).join(' + ') : '—']);
	} else if (borrador.tipo === 'descarte') {
		filas.push(['Bruto', borrador.bruto_kg ? `${kg(borrador.bruto_kg * 1000)} kg` : '—'], ['Carros', (borrador.carros ?? []).map(nombreDeCarroDeDescarte).join(' + ') || '—']);
	} else if (borrador.tipo === 'embolsar') {
		filas.push(['Precinto', borrador.precinto?.precinto ?? '—'], ['Bolsas', borrador.bolsas === null ? '—' : String(borrador.bolsas)]);
	}
	for (const [etiqueta, valor] of filas) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		detalle.appendChild(fila);
	}
	cuerpo.appendChild(detalle);
	if (cuantos > 1) {
		const otro = el('button', { class: 'enlace', type: 'button' }, cuantos === 2 ? 'Ver lo otro que quedó' : 'Ver lo siguiente que quedó');
		otro.onclick = () => irA({ tipo: 'retomar', borradores: vista.borradores, indice: (indice + 1) % cuantos });
		cuerpo.appendChild(otro);
	}

	const seguir = el('button', { class: 'btn go', type: 'button' }, 'Seguir');
	seguir.onclick = async () => {
		// Renueva la marca de actividad: el control de inactividad no re-pregunta al toque.
		await guardarBorrador(borrador);
		if (borrador.tipo === 'descarte') irA({ tipo: 'descarte', borrador });
		else if (borrador.tipo === 'viaje') irA({ tipo: 'captura', borrador });
		else if (borrador.tipo === 'muestra') irA({ tipo: 'calidad-hoja', borrador });
		else if (esBorradorDeEnvasado(borrador)) irA({ tipo: 'envasado', borrador });
		else irA({ tipo: 'produccion', borrador });
	};
	const descartar = el('button', { class: 'btn ghost anchoflex', type: 'button' }, 'Empezar de nuevo');
	descartar.onclick = async () => {
		// SOLO este borrador — jamás los de otra pestaña (A5 de la ronda del diff);
		// si quedan otros, se ofrecen después.
		await descartarBorrador(borrador);
		const restantes = await listarBorradores();
		if (restantes.length > 0) irA({ tipo: 'retomar', borradores: restantes });
		else acciones.nuevaCaptura();
	};
	pie.appendChild(descartar);
	pie.appendChild(seguir);
}

export function pintarAdvertenciaReloj(cuerpo: HTMLElement, pie: HTMLElement, vista: Extract<Vista, { tipo: 'advertencia-reloj' }>): void {
	const texto = textoDelAviso();
	cuerpo.appendChild(el('div', { class: 'q arriba' }, 'La hora del teléfono está mal'));
	cuerpo.appendChild(
		flag(
			'stop',
			`Este teléfono está corrido ${vista.desvioMinutos} minutos`,
			`contra la hora de la oficina. ${texto.queSeArruina} Arreglá la hora del teléfono (fecha y hora automáticas) antes de cargar.`,
		),
	);
	const igual = el('button', { class: 'btn ghost anchoflex', type: 'button' }, texto.seguir);
	igual.onclick = () => {
		aceptarReloj();
		irA(vista.luego);
	};
	const listo = el('button', { class: 'btn go', type: 'button' }, 'Ya la arreglé');
	listo.onclick = () => window.location.reload();
	pie.appendChild(igual);
	pie.appendChild(listo);
}

export function pintarGuardado(cuerpo: HTMLElement, pie: HTMLElement, vista: Extract<Vista, { tipo: 'guardado' }>): void {
	const esDescarte = vista.entidad === 'descarte';
	const caja = el('div', { class: 'done' });
	const anillo = el('div', { class: 'ring' });
	anillo.appendChild(icono('check', 52, 2.6));
	caja.appendChild(anillo);
	caja.appendChild(
		el('h2', undefined, esDescarte ? 'Descarte guardado' : vista.nroViaje !== null ? `Viaje ${vista.nroViaje} guardado` : 'Viaje guardado'),
	);
	const ticket = el('div', { class: 'ticket' });
	const fila = el('div', { class: 'row grandeticket' });
	fila.appendChild(el('span', undefined, esDescarte ? 'Se fue al monte' : 'Entregaste'));
	fila.appendChild(el('b', undefined, `${kg(vista.netoG)} kg`));
	ticket.appendChild(fila);
	caja.appendChild(ticket);

	const linea = el('div', { class: 'syncline' });
	linea.appendChild(icono('subir', 17, 2.2));
	linea.appendChild(
		el(
			'span',
			vista.enviado ? undefined : { class: 'ambar' },
			vista.enviado ? 'Enviado a la oficina' : 'Guardado en el teléfono · sube solo cuando haya señal',
		),
	);
	caja.appendChild(linea);
	cuerpo.appendChild(caja);

	const otro = el('button', { class: 'btn go ancho', type: 'button' }, 'Otro viaje');
	otro.onclick = () => acciones.nuevaCaptura();
	pie.appendChild(otro);
}

/** Filtro por persona de «cargados desde este teléfono»; vive mientras dure la sesión de pantalla. */
let personaFiltro: string | null = null;

export async function pintarPendientes(cuerpo: HTMLElement, pie: HTMLElement): Promise<void> {
	// En verde y en producción, con el bloque del servidor, la pantalla es «Verde, últimas 24 horas» (§5.5, §12.11) o
	// «Producción, últimas 24 horas» (§12.20): lo de todos los teléfonos de la estación. Sin el bloque (app o
	// servidor viejos), lo de siempre: lo propio.
	const estacion = app.sesion?.estacion;
	const bloqueReciente = estacion === 'verde' ? app.catalogos?.verde_reciente : estacion === 'produccion' ? app.catalogos?.produccion_reciente : null;
	const reciente = !!bloqueReciente;
	cuerpo.appendChild(el('div', { class: 'q arriba' }, reciente ? `${estacion === 'verde' ? 'Verde' : 'Producción'}, últimas 24 horas` : 'Lo cargado en este teléfono'));
	if (bloqueReciente) {
		cuerpo.appendChild(el('div', { class: 'qsub aparte' }, `Desde ${cuandoALas(bloqueReciente.desde)} · todos los teléfonos de ${estacion === 'verde' ? 'verde' : 'producción'}.`));
	}
	const { pendientes, incidentes } = await contarPendientes(app.bd);
	const { fotos, fotosFalladas } = await contarFotos(app.bd);
	const grupos = await pendientesPorEpoch(app.bd);
	const filas = grupos.flatMap((g) => g.filas);
	const congelados = new Set(app.preferencias.epochs_congelados ?? []);
	const esperas = esperasDePadre(filas, congelados);
	const retenidos = filas.filter((f) => congelados.has(f.epoch)).length;
	const bloqueados = esperas.filter((e) => e.padreCongelado && !congelados.has(e.hijo.epoch));
	const idsBloqueados = new Set(bloqueados.map((e) => e.hijo.id));
	const senal = filas.filter((f) => !congelados.has(f.epoch) && !idsBloqueados.has(f.id));
	const porSenal = senal.length;
	if (pendientes === 0 && incidentes === 0 && fotos === 0 && fotosFalladas === 0) {
		cuerpo.appendChild(flag('ok', 'Todo subió a la oficina.', ''));
	}
	if (retenidos > 0 || bloqueados.length > 0) {
		const cuantos = retenidos > 0 ? `${retenidos} registro${retenidos > 1 ? 's' : ''} de un enrolamiento anterior` : `${bloqueados.length} registro${bloqueados.length > 1 ? 's' : ''} esperando a uno de un enrolamiento anterior`;
		cuerpo.appendChild(flag('warn', `${cuantos}.`, 'No es falta de señal. Avisale a gestión.'));
		pintarListaTrabas(cuerpo, trabasCongeladas(filas, congelados));
	}
	if (porSenal > 0) {
		const masViejaMs = senal.reduce<number | null>((min, f) => {
			const ms = Date.parse(f.creado_en);
			return Number.isNaN(ms) ? min : Math.min(min ?? ms, ms);
		}, null);
		const horas = masViejaMs === null ? 0 : Math.floor((Date.now() - masViejaMs) / 3_600_000);
		cuerpo.appendChild(
			flag(
				horas >= 12 ? 'warn' : 'ok',
				`${porSenal} registro${porSenal > 1 ? 's' : ''} esperando señal.`,
				horas >= 1 ? `El más viejo hace ${horas} h. Suben solos cuando haya conexión.` : 'Suben solos cuando haya conexión.',
			),
		);
	}
	// Cada rechazo con qué registro y por qué: «hay un problema con algún registro» mandaba a buscar el
	// motivo a la configuración, que es del encargado (primer día real, 2/10).
	if (incidentes > 0) {
		for (const incidente of await bandeja(app.bd)) {
			cuerpo.appendChild(
				flag('stop', `No entró: ${describirResumen(incidente.resumen).titulo}.`, `${queMotivo(incidente.motivo)}. Avisale al encargado: lo quita o lo rehace desde la configuración.`),
			);
		}
	}
	if (fotos > 0) {
		// Un teléfono que dejó de ser de producción no puede subirlas (403): quedan retenidas y se dice.
		const puedeSubirlas = app.sesion?.estacion === 'produccion';
		cuerpo.appendChild(
			flag(
				puedeSubirlas ? 'ok' : 'warn',
				`${fotos} foto${fotos > 1 ? 's' : ''} del precinto por subir.`,
				puedeSubirlas ? 'Suben solas después del silo, cuando haya conexión.' : 'Este teléfono ya no es de producción: quedan hasta que vuelva a serlo, o el encargado las quita en la configuración.',
			),
		);
	}
	if (fotosFalladas > 0) {
		cuerpo.appendChild(flag('stop', `${fotosFalladas} foto${fotosFalladas > 1 ? 's' : ''} del precinto no pudo subir.`, 'Avisale al encargado (el detalle está en la configuración).'));
	}
	const avisoPersistencia = avisoDePersistencia(app.preferencias.persistencia_ok, abiertaComoApp());
	if (avisoPersistencia !== null) cuerpo.appendChild(flag('warn', avisoPersistencia[0], avisoPersistencia[1]));
	if (app.preferencias.ultimo_error_sync && retenidos === 0 && bloqueados.length === 0) {
		cuerpo.appendChild(flag('stop', 'Error técnico al subir.', 'Avisale al encargado (el detalle está en la configuración).'));
	}
	if (!(await listoSinSenal())) {
		cuerpo.appendChild(
			flag('warn', 'Todavía no está listo para trabajar sin señal.', 'Abrí la app una vez con conexión para que se prepare.'),
		);
	}
	// Espacio del teléfono (best-effort): si el origen va lleno, avisar antes del golpe.
	if (navigator.storage?.estimate) {
		const { usage, quota } = await navigator.storage.estimate().catch(() => ({ usage: 0, quota: 0 }));
		if (quota && usage && usage / quota > 0.8) {
			cuerpo.appendChild(
				flag('warn', 'Queda poco espacio en el teléfono.', 'Avisale al encargado antes de que no se puedan guardar viajes.'),
			);
		}
	}

	const pintado = reciente && (estacion === 'verde' ? await pintarVerdeReciente(cuerpo) : await pintarProduccionReciente(cuerpo));
	if (!pintado) await pintarCargadosHoy(cuerpo);

	// Un lote o una persona recién dados de alta tienen que aparecer en el turno,
	// no dentro de seis horas: el refresco a la vista, con su fecha.
	const bajados = app.preferencias.catalogos_bajados_en;
	const actualizar = el(
		'button',
		{ class: 'btn ghost ancho', type: 'button' },
		`Actualizar listas${bajados ? ` (última vez ${horaCorta(bajados)})` : ''}`,
	);
	actualizar.onclick = async () => {
		await actualizarPreferencias(app.bd, { catalogos_bajados_en: 0 });
		app.preferencias.catalogos_bajados_en = 0;
		app.dispararSync();
		app.render();
	};
	cuerpo.appendChild(actualizar);

	const volver = el('button', { class: 'btn go ancho', type: 'button' }, 'Volver');
	volver.onclick = () => acciones.nuevaCaptura();
	pie.appendChild(volver);
}

/**
 * Carros y kilos de la jornada, por persona (visita 5/9, r. 17): el reporte
 * chico que hoy cada uno anota en un papel propio. Cuenta lo que salió de ESTE
 * teléfono —recibos y pendientes—; una corrección hecha desde la oficina no
 * baja acá, y la pantalla lo dice.
 */
function numeroDelResumen(r: Resumen): number | null {
	return r.entidad === 'pallet' && typeof r.numero === 'number' ? r.numero : null;
}

async function pintarCargadosHoy(cuerpo: HTMLElement): Promise<void> {
	const { fecha, recibos, pendientes } = await cargadosEnLaJornada(app.bd, ahoraMs());
	const grupos = await pendientesPorEpoch(app.bd);
	const congelados = new Set(app.preferencias.epochs_congelados ?? []);
	const porHijo = new Map(esperasDePadre(grupos.flatMap((g) => g.filas), congelados).map((e) => [e.hijo.id, e]));
	const registros: Array<{ id: string | null; personas: string[]; resumen: Recibo['resumen']; nro: number | null; subido: boolean; epoch?: string; hojaDeHoy?: boolean }> = [
		// El número del pallet viene con el acuse de la apertura y las transiciones lo conservan (§5.1); un
		// cierre de un pallet del catálogo lo lleva en su resumen desde que se encola.
		...recibos.map((r) => ({
			id: null as string | null,
			personas: r.personas,
			resumen: r.resumen,
			nro: r.nro_viaje ?? r.nro_muestra ?? r.numero ?? numeroDelResumen(r.resumen),
			subido: true,
			// §12.17: una hoja de hoy cuyo recibo pisó su humedad o su corrección sigue contando como muestra.
			hojaDeHoy: r.cadena?.hoja === fecha,
		})),
		...pendientes.map((f) => ({ id: f.id, personas: f.personas, resumen: f.resumen, nro: numeroDelResumen(f.resumen), subido: false, epoch: f.epoch })),
	];
	cuerpo.appendChild(el('div', { class: 'qsub arriba' }, 'Cargado desde este teléfono en la jornada de hoy:'));
	if (registros.length === 0) {
		cuerpo.appendChild(el('div', { class: 'qsub' }, 'Todavía nada.'));
		return;
	}

	const personas = [...new Set(registros.flatMap((r) => r.personas))];
	if (personas.length > 1) {
		const chips = el('div', { class: 'chips' });
		chips.appendChild(chip('Todos', personaFiltro === null, () => {
			personaFiltro = null;
			app.render();
		}));
		for (const id of personas) {
			const nombre = app.catalogos?.personas.find((p) => p.id === id)?.nombre ?? '—';
			chips.appendChild(
				chip(nombre, personaFiltro === id, () => {
					personaFiltro = id;
					app.render();
				}),
			);
		}
		cuerpo.appendChild(chips);
	}
	const visibles = personaFiltro === null ? registros : registros.filter((r) => r.personas.includes(personaFiltro as string));

	let viajes = 0;
	let carros = 0;
	let netoViajes = 0;
	let descartes = 0;
	let netoDescarte = 0;
	const cuenta = new Map<Entidad, number>();
	let minutosParada = 0;
	let bolsasCargadas = 0;
	// v6: un cambio de calidad desde envasado (§12.5) y una humedad completada (§12.9) viajan como
	// `muestra`, pero no son muestras tomadas: se cuentan aparte.
	let cambiosDeCalidad = 0;
	let humedades = 0;
	let humedadesCorregidas = 0;
	let hojasCorregidas = 0;
	let hojasPisadas = 0;
	for (const r of visibles) {
		if (r.resumen.entidad === 'muestra' && r.resumen.accion !== undefined && r.hojaDeHoy === true) hojasPisadas += 1;
		cuenta.set(r.resumen.entidad, (cuenta.get(r.resumen.entidad) ?? 0) + 1);
		if (r.resumen.entidad === 'muestra' && r.resumen.accion === 'cambio_letra') cambiosDeCalidad += 1;
		else if (r.resumen.entidad === 'muestra' && r.resumen.accion === 'humedad') humedades += 1;
		else if (r.resumen.entidad === 'muestra' && r.resumen.accion === 'humedad_corregida') humedadesCorregidas += 1;
		else if (r.resumen.entidad === 'muestra' && r.resumen.accion === 'hoja_corregida') hojasCorregidas += 1;
		if (r.resumen.entidad === 'viaje_verde') {
			viajes += 1;
			carros += r.resumen.cant_carros;
			netoViajes += r.resumen.neto_g;
		} else if (r.resumen.entidad === 'descarte') {
			descartes += 1;
			netoDescarte += r.resumen.neto_g;
		} else if (r.resumen.entidad === 'parada') {
			minutosParada += r.resumen.minutos;
		} else if (r.resumen.entidad === 'envasado' && r.resumen.accion === 'cargo') {
			bolsasCargadas += r.resumen.bolsas;
		}
	}
	const totales = el('div', { class: 'ticket' });
	const filas: Array<[string, string]> = [];
	// Verde muestra sus dos líneas siempre, aunque estén en cero: es la estación
	// donde «hoy no entró nada» es información. Producción suma las suyas si las hay.
	if (viajes > 0 || app.sesion?.estacion === 'verde') {
		filas.push(['Viajes', `${viajes} · ${carros} carro${carros === 1 ? '' : 's'}`], ['Kilos netos', `${kg(netoViajes)} kg`]);
	}
	if (descartes > 0) filas.push(['Descarte', `${descartes} · ${kg(netoDescarte)} kg`]);
	const silos = cuenta.get('llenado') ?? 0;
	const turnos = cuenta.get('parcial') ?? 0;
	const cambios = cuenta.get('cambio_carro') ?? 0;
	const paradas = cuenta.get('parada') ?? 0;
	const muestras = (cuenta.get('muestra') ?? 0) - cambiosDeCalidad - humedades - humedadesCorregidas - hojasCorregidas + hojasPisadas;
	if (silos > 0) filas.push(['Silos', `${silos} movimiento${silos === 1 ? '' : 's'}`]);
	if (turnos > 0) filas.push(['Pesadas', String(turnos)]);
	if (cambios > 0) filas.push(['Cambios de carro', String(cambios)]);
	if (paradas > 0) filas.push(['Paradas', `${paradas} · ${minutosParada} min`]);
	if (muestras > 0) filas.push(['Muestras', String(muestras)]);
	if (humedades > 0) filas.push(['Humedades completadas', String(humedades)]);
	if (humedadesCorregidas > 0) filas.push(['Humedades corregidas', String(humedadesCorregidas)]);
	if (hojasCorregidas > 0) filas.push(['Hojas corregidas', String(hojasCorregidas)]);
	if (cambiosDeCalidad > 0) filas.push(['Calidad cambiada', String(cambiosDeCalidad)]);
	// Envasado: sin estas dos, una jornada con cargas y cierres decía «Registros 0» (Codex, 14/9).
	const pallets = cuenta.get('pallet') ?? 0;
	const cargas = cuenta.get('envasado') ?? 0;
	if (pallets > 0) filas.push(['Pallets', `${pallets} movimiento${pallets === 1 ? '' : 's'}`]);
	if (cargas > 0) filas.push(['Cargas', `${cargas} · ${bolsasCargadas} bolsa${bolsasCargadas === 1 ? '' : 's'}`]);
	if (filas.length === 0) filas.push(['Registros', '0']);
	for (const [etiqueta, valor] of filas) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		totales.appendChild(fila);
	}
	cuerpo.appendChild(totales);

	const lista = el('div', { class: 'sum' });
	for (const r of visibles) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		const visto = describirResumen(r.resumen);
		const nota = r.subido ? '' : ` · ${notaDePendiente(r.epoch, r.id === null ? undefined : porHijo.get(r.id), congelados)}`;
		textos.appendChild(el('div', { class: 'lb' }, `${visto.titulo}${nota}`));
		textos.appendChild(
			el('div', { class: 'vl' }, `${r.nro !== null ? `${r.resumen.entidad === 'muestra' ? 'Muestra' : r.resumen.entidad === 'pallet' ? 'Pallet Nº' : 'Viaje'} ${r.nro} · ` : ''}${visto.detalle}`),
		);
		fila.appendChild(textos);
		lista.appendChild(fila);
	}
	cuerpo.appendChild(lista);
	cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Si la oficina corrigió algo, acá no se ve.'));
}

/** Un snapshot de más de diez minutos ya no dice «ahora»: la pantalla avisa de cuándo es (§5.5). */
const SNAPSHOT_VIEJO_MS = 10 * MINUTO_MS;

/** «hoy a las 20:31», «ayer a las 20:31», «3/10 a las 20:31». */
function cuandoALas(instante: string): string {
	const corto = cuandoCorto(instante, ahoraMs(), true);
	const corte = corto.lastIndexOf(' ');
	return `${corto.slice(0, corte)} a las ${corto.slice(corte + 1)}`;
}

function nombreDePersona(id: string): string {
	return app.catalogos?.personas.find((p) => p.id === id)?.nombre ?? '—';
}

/**
 * Sin señal, o con un snapshot viejo, lo de los otros teléfonos tiene fecha y se dice; lo propio está siempre.
 * Devuelve cuándo se bajó el catálogo (ms del reloj local; 0 si nunca), para el pie de la pantalla.
 */
function flagDeSnapshot(cuerpo: HTMLElement): number {
	const bajados = app.preferencias.catalogos_bajados_en ?? 0;
	const marca = app.preferencias.catalogos_hora_servidor;
	const horaDelSnapshot = marca === undefined || Number.isNaN(Date.parse(marca)) ? null : instanteLocal(Date.parse(marca));
	if (!app.enLinea || Date.now() - bajados > SNAPSHOT_VIEJO_MS) {
		const de = horaDelSnapshot === null ? 'de una hora desconocida' : `de las ${cuandoCorto(horaDelSnapshot, ahoraMs())}`;
		cuerpo.appendChild(
			flag('warn', app.enLinea ? 'Las listas no se actualizan hace rato.' : 'Sin señal.', `Lo de los otros teléfonos es ${de}; lo de este teléfono está al día.`),
		);
	}
	return bajados;
}

/** Los chips por persona de las listas recientes, si hay más de una; el filtro vive en `personaFiltro`. */
function chipsDePersonas(cuerpo: HTMLElement, personas: string[]): void {
	if (personas.length <= 1) return;
	const chips = el('div', { class: 'chips arriba' });
	chips.appendChild(
		chip('Todos', personaFiltro === null, () => {
			personaFiltro = null;
			app.render();
		}),
	);
	for (const id of personas) {
		chips.appendChild(
			chip(nombreDePersona(id), personaFiltro === id, () => {
				personaFiltro = id;
				app.render();
			}),
		);
	}
	cuerpo.appendChild(chips);
}

/** Filtro por tipo de «Producción, últimas 24 horas» (§12.20): todo, o una de las cuatro cosas que registra la estación. */
let tipoFiltro: FilaProduccionReciente['entidad'] | null = null;
const TIPOS_DE_PRODUCCION: Array<[FilaProduccionReciente['entidad'], string]> = [
	['llenado', 'Silos'],
	['parcial', 'Pesadas'],
	['parada', 'Paradas'],
	['cambio_carro', 'Carros'],
];

/** El «hoy», «ayer» o el día de un instante, sin la hora: una parada se fecha por su turno, no por una hora. */
function diaDe(instante: string): string {
	const corto = cuandoCorto(instante, ahoraMs(), true);
	return corto.slice(0, corto.lastIndexOf(' '));
}

function numeroDeSiloFisico(id: string | null): string {
	const silo = id === null ? undefined : (app.catalogos?.silos ?? []).find((s) => s.id === id);
	return silo === undefined ? '—' : String(silo.numero);
}

function nombreDeMotivoDeParada(id: string | null): string {
	return (id === null ? undefined : (app.catalogos?.motivos_parada ?? []).find((m) => m.id === id)?.nombre) ?? '—';
}

const PORQUE_NO_SUMA: Record<MotivoNoSuma, string> = { blendeo: 'reproceso', descartado: 'silo descartado', lote_desconocido: 'lote desconocido' };

/** Cómo se lee una fila de «Producción, últimas 24 horas»: las del catálogo no son `Resumen`, así que no pasan por `describirResumen`. */
function describirFilaReciente(f: FilaProduccionReciente): { titulo: string; detalle: string } {
	const cuando = (instante: string | null) => (instante === null ? '—' : cuandoCorto(instante, ahoraMs(), true));
	switch (f.entidad) {
		case 'llenado': {
			const partes: string[] = [];
			if (f.abierto_en !== null) partes.push(`abierto ${cuando(f.abierto_en)}`);
			if (f.estado === 'cerrado') partes.push(`cerrado ${cuando(f.cerrado_en)}`, f.neto_g === null ? 'sin pesar' : `${kg(f.neto_g)} kg netos`);
			else if (f.estado === 'descartado') partes.push(`descartado ${cuando(f.cerrado_en)}${f.motivo_descarte ? ` · ${f.motivo_descarte}` : ''}`);
			else partes.push('sigue abierto', f.ultimo_bruto_g === null ? 'sin pesar' : `${kg(f.ultimo_bruto_g)} kg brutos`);
			if (f.lote_campo_id !== null) partes.push(`lote ${(app.catalogos?.lotes_campo ?? []).find((l) => l.id === f.lote_campo_id)?.nombre ?? '—'}`);
			if (f.trieur_id !== null) partes.push(`trieur ${(app.catalogos?.trieurs ?? []).find((t) => t.id === f.trieur_id)?.numero ?? '—'}`);
			return { titulo: `Silo ${f.silo ?? numeroDeSiloFisico(f.silo_fisico_id)} · precinto ${f.precinto}`, detalle: partes.join(' · ') };
		}
		case 'parcial': {
			const partes: string[] = [];
			if (f.turno !== null) partes.push(`turno ${NOMBRE_TURNO[f.turno]}`);
			partes.push(`${kg(f.bruto_g)} kg brutos`, `${kg(f.del_turno_g)} kg este turno`);
			if (f.es_cierre) partes.push('al cerrar');
			if (f.recibida_por !== null) partes.push(`recibió ${nombreDePersona(f.recibida_por)}`);
			if (f.no_suma !== null) partes.push(`no suma: ${PORQUE_NO_SUMA[f.no_suma]}`);
			const silo = f.silo_fisico_id === null ? '' : ` · silo ${numeroDeSiloFisico(f.silo_fisico_id)}`;
			return { titulo: `Pesada · precinto ${f.precinto}${silo}`, detalle: partes.join(' · ') };
		}
		case 'parada':
			return { titulo: `Parada · ${f.motivo ?? nombreDeMotivoDeParada(f.motivo_parada_id)}`, detalle: `${f.minutos} min${f.notas ? ` · ${f.notas}` : ''}` };
		case 'cambio_carro': {
			const numero = f.carro ?? String((app.catalogos?.carros ?? []).find((c) => c.id === f.carro_id)?.numero ?? '—');
			return { titulo: `Carro ${numero}`, detalle: 'bajó al proceso' };
		}
	}
}

/**
 * «Producción, últimas 24 horas» (§5.5, §12.20): lo que hicieron todos los teléfonos de producción en la ventana
 * que fijó el servidor —silos, pesadas de turno, paradas y bajadas de carro—, fundido por identidad con lo propio
 * (`proyectarProduccionReciente`). Los bordes son los del snapshot; el reloj del teléfono solo pone el «hoy»/«ayer».
 * Devuelve false si el catálogo no trae el bloque, y la pantalla vuelve a «Lo cargado en este teléfono».
 */
async function pintarProduccionReciente(cuerpo: HTMLElement): Promise<boolean> {
	const bloque = app.catalogos?.produccion_reciente;
	if (!bloque) return false;
	const propios = await propiosDeProduccionDesde(app.bd, bloque.desde);
	// Lo que el teléfono sabe aparte del bloque para decidir si una pesada propia suma (la regla de kilos): el lote de
	// cada silo abierto y el tipo de cada lote. Sin dato, no suma (ronda de disensor sobre el plan, 20/10).
	const contexto = {
		loteDelSilo: new Map((app.catalogos?.llenados_abiertos ?? []).map((l) => [l.llenado_id, l.lote_campo_id] as const)),
		tipoDeLote: new Map((app.catalogos?.lotes_campo ?? []).map((l) => [l.id, (l.tipo ?? 'campo') as TipoDeLote] as const)),
	};
	const proyeccion = proyectarProduccionReciente(bloque, propios, app.preferencias.catalogos_hora_servidor, app.sesion?.dispositivo_id ?? null, contexto);
	const grupos = await pendientesPorEpoch(app.bd);
	const congelados = new Set(app.preferencias.epochs_congelados ?? []);
	const porHijo = new Map(esperasDePadre(grupos.flatMap((g) => g.filas), congelados).map((e) => [e.hijo.id, e]));

	const bajados = flagDeSnapshot(cuerpo);

	// Segmentos por tipo: mantenimiento toca «Paradas» y ve qué se rompió; las encargadas, todo.
	const segmentos = el('div', { class: 'chips arriba' });
	segmentos.appendChild(
		chip('Todo', tipoFiltro === null, () => {
			tipoFiltro = null;
			app.render();
		}),
	);
	for (const [tipo, etiqueta] of TIPOS_DE_PRODUCCION) {
		segmentos.appendChild(
			chip(etiqueta, tipoFiltro === tipo, () => {
				tipoFiltro = tipo;
				app.render();
			}),
		);
	}
	cuerpo.appendChild(segmentos);

	// El ticket cuenta lo que se ve: con un tipo o una persona elegidos, lo de ese recorte (como en verde).
	if (personaFiltro !== null && !proyeccion.personas.includes(personaFiltro)) personaFiltro = null;
	const visibles = proyeccion.filas.filter((f) => (tipoFiltro === null || f.entidad === tipoFiltro) && (personaFiltro === null || f.personas.includes(personaFiltro)));
	const t = totalesDeProduccion(visibles);
	const filasDelTicket: Array<[string, string]> = [];
	if (tipoFiltro === null || tipoFiltro === 'llenado') {
		filasDelTicket.push(['Silos cerrados', `${t.silos_cerrados} · ${kg(t.neto_cerrados_g)} kg netos`]);
		if (t.silos_descartados > 0) filasDelTicket.push(['Descartados', String(t.silos_descartados)]);
	}
	if (tipoFiltro === null || tipoFiltro === 'parcial') {
		const afuera = t.pesadas_que_no_suman === 0 ? '' : ` · ${t.pesadas_que_no_suman} no suma${t.pesadas_que_no_suman === 1 ? '' : 'n'}`;
		filasDelTicket.push(['Kilos de turno', `${t.pesadas} pesada${t.pesadas === 1 ? '' : 's'} · ${kg(t.kilos_turno_g)} kg${afuera}`]);
	}
	if (tipoFiltro === null || tipoFiltro === 'parada') filasDelTicket.push(['Paradas', `${t.paradas} · ${t.minutos_parada} min`]);
	if (tipoFiltro === null || tipoFiltro === 'cambio_carro') filasDelTicket.push(['Carros bajados', String(t.bajadas)]);
	const ticket = el('div', { class: 'ticket' });
	for (const [etiqueta, valor] of filasDelTicket) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		ticket.appendChild(fila);
	}
	cuerpo.appendChild(ticket);

	// Sin filas no hay lista, pero el aviso de «hora fuera de rango» va igual: con la lista vacía y un registro
	// propio fechado adelante, «Todavía nada» escondía el registro y el motivo (Codex, ronda del diff, 20/10).
	if (proyeccion.filas.length === 0) {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Todavía nada en las últimas 24 horas.'));
	} else {
		chipsDePersonas(cuerpo, proyeccion.personas);
		if (visibles.length === 0) cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Nada de eso en las últimas 24 horas.'));
		else cuerpo.appendChild(listaDeProduccion(visibles, porHijo, congelados));
	}
	flagDeFueraDeRango(cuerpo, proyeccion.fuera_de_rango);
	cuerpo.appendChild(
		el('div', { class: 'qsub despues' }, `Lo que corrigió la oficina ya está incluido.${bajados ? ` Actualizado a las ${horaCorta(bajados)}.` : ''}`),
	);
	return true;
}

function listaDeProduccion(visibles: FilaProduccionReciente[], porHijo: Map<string, EsperaDePadre>, congelados: Set<string>): HTMLElement {
	const lista = el('div', { class: 'sum arriba' });
	for (const f of visibles) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		// Una parada se fecha por su turno: su hora es el inicio del turno informado, y «hoy 22:00» engañaría.
		const cuando =
			f.entidad === 'parada' && f.hora !== null
				? `${diaDe(f.hora)}${f.turno === null ? '' : ` · turno ${NOMBRE_TURNO[f.turno]}`}`
				: f.hora === null
					? 'hora al subir'
					: cuandoCorto(f.hora, ahoraMs());
		const etiqueta = el('div', { class: 'lb' }, `${cuando} · ${f.personas.map(nombreDePersona).join(' y ')}`);
		if (f.este_telefono) etiqueta.appendChild(el('span', { class: 'tag' }, 'este teléfono'));
		if (f.corregido) etiqueta.appendChild(el('span', { class: 'tag' }, 'corregido por la oficina'));
		if (f.entidad === 'parcial' && f.no_suma !== null) etiqueta.appendChild(el('span', { class: 'tag' }, 'no suma'));
		if (f.origen === 'pendiente') {
			etiqueta.appendChild(el('span', { class: 'tag ambar' }, notaDePendiente(f.epoch, f.id === undefined ? undefined : porHijo.get(f.id), congelados)));
		}
		textos.appendChild(etiqueta);
		const visto = describirFilaReciente(f);
		textos.appendChild(el('div', { class: 'vl' }, `${visto.titulo} · ${visto.detalle}`));
		fila.appendChild(textos);
		lista.appendChild(fila);
	}
	return lista;
}

/** Lo propio fechado después de «hasta» no se lista ni se suma: se dice, haya lista o no. */
function flagDeFueraDeRango(cuerpo: HTMLElement, n: number): void {
	if (n === 0) return;
	cuerpo.appendChild(
		flag('warn', `${n} registro${n === 1 ? '' : 's'} de este teléfono con hora fuera de rango.`, 'Revisá el reloj del teléfono. Se van a ver cuando la hora los alcance.'),
	);
}

/**
 * «Verde, últimas 24 horas» (§5.5, §12.11): lo que cargaron todos los teléfonos de verde en la ventana
 * que fijó el servidor, fundido por identidad con lo propio (`proyectarVerdeReciente`). Los bordes son
 * los del snapshot; el reloj del teléfono solo pone el «hoy»/«ayer» de cada fila. Devuelve false si el
 * catálogo no trae el bloque, y la pantalla vuelve a «Lo cargado en este teléfono».
 */
async function pintarVerdeReciente(cuerpo: HTMLElement): Promise<boolean> {
	const bloque = app.catalogos?.verde_reciente;
	if (!bloque) return false;
	const propios = await propiosDeVerdeDesde(app.bd, bloque.desde);
	const proyeccion = proyectarVerdeReciente(bloque, propios, app.preferencias.catalogos_hora_servidor, app.sesion?.dispositivo_id ?? null);
	const grupos = await pendientesPorEpoch(app.bd);
	const congelados = new Set(app.preferencias.epochs_congelados ?? []);
	const porHijo = new Map(esperasDePadre(grupos.flatMap((g) => g.filas), congelados).map((e) => [e.hijo.id, e]));

	const bajados = flagDeSnapshot(cuerpo);

	// El ticket cuenta lo que se ve: con una persona elegida, lo de esa persona, como en «Lo cargado en este
	// teléfono» (Codex, ronda del diff, 5/10). Un filtro que quedó de otra visita y ya no aparece, se suelta.
	if (personaFiltro !== null && !proyeccion.personas.includes(personaFiltro)) personaFiltro = null;
	const visibles = personaFiltro === null ? proyeccion.filas : proyeccion.filas.filter((f) => f.personas.includes(personaFiltro as string));
	const totales = totalesDe(visibles);
	const ticket = el('div', { class: 'ticket' });
	const filasDelTicket: Array<[string, string]> = [
		['Viajes', `${totales.viajes} · ${totales.carros} carro${totales.carros === 1 ? '' : 's'}`],
		['Kilos netos', `${kg(totales.neto_viajes_g)} kg`],
		['Descarte', `${totales.descartes} · ${kg(totales.neto_descarte_g)} kg`],
	];
	for (const [etiqueta, valor] of filasDelTicket) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		ticket.appendChild(fila);
	}
	cuerpo.appendChild(ticket);

	// Sin filas no hay lista, pero el aviso de «hora fuera de rango» va igual (Codex, ronda del diff de producción, 20/10).
	if (proyeccion.filas.length === 0) {
		cuerpo.appendChild(el('div', { class: 'qsub despues' }, 'Todavía nada en las últimas 24 horas.'));
	} else {
		chipsDePersonas(cuerpo, proyeccion.personas);
		const lista = el('div', { class: 'sum arriba' });
		for (const f of visibles) {
			const fila = el('div', { class: 'r' });
			const textos = el('div');
			const cuando = f.hora === null ? 'hora al subir' : cuandoCorto(f.hora, ahoraMs());
			const etiqueta = el('div', { class: 'lb' }, `${cuando} · ${f.personas.map(nombreDePersona).join(' y ')}`);
			if (f.este_telefono) etiqueta.appendChild(el('span', { class: 'tag' }, 'este teléfono'));
			if (f.corregido) etiqueta.appendChild(el('span', { class: 'tag' }, 'corregido por la oficina'));
			if (f.origen === 'pendiente') {
				etiqueta.appendChild(el('span', { class: 'tag ambar' }, notaDePendiente(f.epoch, f.id === undefined ? undefined : porHijo.get(f.id), congelados)));
			}
			textos.appendChild(etiqueta);
			const detalle =
				f.entidad === 'viaje_verde'
					? `${f.nro_viaje === null ? 'Viaje' : `Viaje ${f.nro_viaje}`} · ${f.lote ?? '—'} · ${f.carros} · ${kg(f.neto_g)} kg`
					: `Descarte · ${kg(f.neto_g)} kg al monte`;
			textos.appendChild(el('div', { class: 'vl' }, detalle));
			fila.appendChild(textos);
			lista.appendChild(fila);
		}
		cuerpo.appendChild(lista);
	}

	flagDeFueraDeRango(cuerpo, proyeccion.fuera_de_rango);
	cuerpo.appendChild(
		el('div', { class: 'qsub despues' }, `Lo que corrigió la oficina ya está incluido.${bajados ? ` Actualizado a las ${horaCorta(bajados)}.` : ''}`),
	);
	return true;
}

export async function pintarConfig(cuerpo: HTMLElement, pie: HTMLElement): Promise<void> {
	cuerpo.appendChild(el('div', { class: 'q arriba' }, 'Configuración'));
	cuerpo.appendChild(el('div', { class: 'qsub' }, 'Pantalla del encargado.'));

	const datos = el('div', { class: 'ticket' });
	const filas: Array<[string, string]> = [
		['Teléfono', app.sesion?.nombre_dispositivo ?? '—'],
		['Estación', app.sesion?.estacion ?? 'sin estación'],
		['Versión', __APP_VERSION__],
		['Último sync', app.preferencias.hora_ultimo_sync ?? 'nunca'],
		['Desvío del reloj', app.preferencias.desvio_ms === undefined ? '—' : `${Math.round(app.preferencias.desvio_ms / 1000)} s`],
	];
	if (app.preferencias.ultimo_error_sync) {
		filas.push(['Último error de sync', app.preferencias.ultimo_error_sync]);
	}
	for (const [etiqueta, valor] of filas) {
		const fila = el('div', { class: 'row' });
		fila.appendChild(el('span', undefined, etiqueta));
		fila.appendChild(el('b', undefined, valor));
		datos.appendChild(fila);
	}
	cuerpo.appendChild(datos);

	const incidentes = await bandeja(app.bd);
	if (incidentes.length > 0) {
		cuerpo.appendChild(el('div', { class: 'qsub arriba' }, 'Incidentes (también quedaron registrados en el servidor):'));
		for (const incidente of incidentes) {
			cuerpo.appendChild(tarjetaIncidente(incidente));
		}
	}
	const fotosFalladas = await bandejaDeFotos(app.bd);
	if (fotosFalladas.length > 0) {
		cuerpo.appendChild(el('div', { class: 'qsub arriba' }, 'Fotos del precinto que no pudieron subir:'));
		for (const foto of fotosFalladas) {
			cuerpo.appendChild(tarjetaFotoFallada(foto));
		}
	}
	// Retenidas: listas para subir, pero este teléfono ya no es de producción. Se pueden quitar.
	if (app.sesion?.estacion !== 'produccion') {
		const retenidas = await fotosParaSubir(app.bd);
		if (retenidas.length > 0) {
			cuerpo.appendChild(el('div', { class: 'qsub arriba' }, 'Fotos del precinto retenidas (este teléfono ya no es de producción):'));
			for (const foto of retenidas) {
				cuerpo.appendChild(tarjetaFotoFallada(foto));
			}
		}
	}
	if (app.preferencias.ultimo_error_fotos) {
		cuerpo.appendChild(flag('warn', 'La última foto no se pudo confirmar.', app.preferencias.ultimo_error_fotos));
	}

	const congelados = app.preferencias.epochs_congelados ?? [];
	if (congelados.length > 0) {
		const grupos = await pendientesPorEpoch(app.bd);
		const filas = grupos.flatMap((g) => g.filas);
		const retenidos = filas.filter((f) => congelados.includes(f.epoch)).length;
		if (retenidos > 0) {
			cuerpo.appendChild(
				flag(
					'warn',
					`${retenidos} registro${retenidos > 1 ? 's' : ''} de un enrolamiento anterior.`,
					'No se reenvían solos: revisalos contra la oficina antes de liberar (los ya recibidos van a aparecer como conflicto).',
				),
			);
			const trabas = trabasCongeladas(filas, congelados);
			pintarListaTrabas(cuerpo, trabas);
			const liberar = el('button', { class: 'btn ghost ancho', type: 'button' }, 'Reenviarlos ahora');
			liberar.onclick = () => void reenviarCongelados();
			cuerpo.appendChild(liberar);
		}
	}

	const reenrolar = el('button', { class: 'btn ghost ancho', type: 'button' }, 'Re-enrolar con un código nuevo');
	reenrolar.onclick = () => irA({ tipo: 'enrolar' });
	cuerpo.appendChild(reenrolar);

	const volver = el('button', { class: 'btn go ancho', type: 'button' }, 'Volver');
	volver.onclick = () => acciones.nuevaCaptura();
	pie.appendChild(volver);
}

/** Los retenidos (su epoch está congelado), cada uno con los hijos de un epoch vivo que traba, si tiene. */
function trabasCongeladas(filas: FilaOutbox[], congelados: Iterable<string>): Array<{ padre: FilaOutbox; hijos: FilaOutbox[] }> {
	const helados = new Set(congelados);
	const porPadre = new Map<string, { padre: FilaOutbox; hijos: FilaOutbox[] }>();
	for (const fila of filas) if (helados.has(fila.epoch)) porPadre.set(fila.id, { padre: fila, hijos: [] });
	for (const espera of esperasDePadre(filas, helados).filter((e) => e.padreCongelado && !helados.has(e.hijo.epoch))) {
		porPadre.get(espera.padre.id)?.hijos.push(espera.hijo);
	}
	return [...porPadre.values()];
}

function pintarListaTrabas(cuerpo: HTMLElement, trabas: Array<{ padre: FilaOutbox; hijos: FilaOutbox[] }>): void {
	if (trabas.length === 0) return;
	cuerpo.appendChild(el('div', { class: 'qsub arriba' }, 'Estos no suben hasta que gestión los reenvíe:'));
	const lista = el('div', { class: 'sum' });
	for (const { padre, hijos } of trabas) {
		const fila = el('div', { class: 'r' });
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, describirResumen(padre.resumen).titulo));
		textos.appendChild(el('div', { class: 'vl' }, hijos.length === 0 ? describirResumen(padre.resumen).detalle : `traba ${hijos.map((h) => describirResumen(h.resumen).titulo).join('; ')}`));
		fila.appendChild(textos);
		lista.appendChild(fila);
	}
	cuerpo.appendChild(lista);
}

async function reenviarCongelados(): Promise<void> {
	app.preferencias.epochs_congelados = [];
	await actualizarPreferencias(app.bd, { epochs_congelados: [] });
	app.dispararSync();
	app.render();
}

/**
 * Cómo se lee un registro que todavía no subió: retenido (epoch congelado) gana
 * a esperar al padre; «esperando señal» queda para lo que sí puede viajar.
 */
function notaDePendiente(epoch: string | undefined, espera: EsperaDePadre | undefined, congelados: Set<string>): string {
	if (epoch !== undefined && congelados.has(epoch)) return 'retenido: avisale a gestión';
	if (espera) return `espera a ${describirResumen(espera.padre.resumen).titulo}`;
	return 'esperando señal';
}

/**
 * Cómo se lee un registro cargado en este teléfono, sea de la estación que sea.
 * Una sola función para las tres pantallas que los muestran: sumar una entidad
 * es sumar un caso acá, y el switch exhaustivo sobre el union lo exige — sin
 * esto, un registro de producción se dibujaba como si fuera un descarte.
 */
function describirResumen(r: Resumen): { titulo: string; detalle: string } {
	switch (r.entidad) {
		case 'viaje_verde':
			return { titulo: `${r.lote} · ${r.carros}`, detalle: `${kg(r.neto_g)} kg netos` };
		case 'descarte':
			return { titulo: 'Descarte', detalle: `${kg(r.neto_g)} kg al monte` };
		case 'llenado':
			return {
				titulo: `Silo ${r.silo} · precinto ${r.precinto}`,
				detalle:
					r.accion === 'abrio'
						? 'silo abierto'
						: r.accion === 'descarto'
							? 'silo descartado'
							: `silo cerrado · ${r.neto_g === null ? 'sin pesar' : `${kg(r.neto_g)} kg netos`}`,
			};
		case 'parcial':
			return { titulo: `Pesada del precinto ${r.precinto}`, detalle: `${kg(r.bruto_g)} kg brutos · ${kg(r.del_turno_g)} kg este turno` };
		case 'cambio_carro':
			return { titulo: `Carro ${r.carro}`, detalle: 'bajó al proceso' };
		case 'parada':
			return { titulo: `Parada · ${r.motivo}`, detalle: `${r.minutos} min` };
		case 'muestra':
			// v6: el cambio de calidad desde envasado y la humedad completada no son muestras tomadas.
			return r.accion === 'cambio_letra'
				? { titulo: `Calidad cambiada · silo ${r.silo} · precinto ${r.precinto}`, detalle: `pasa a ${NOMBRE_LETRA[r.letra]}` }
				: r.accion === 'humedad'
					? { titulo: `Humedad completada · silo ${r.silo} · precinto ${r.precinto}`, detalle: NOMBRE_LETRA[r.letra] }
					: r.accion === 'humedad_corregida'
						? // §12.17: con la humedad que se intentó y la que había, para rehacerla a mano si no entró.
							{
								titulo: `Humedad corregida · silo ${r.silo} · precinto ${r.precinto}`,
								detalle: `${r.humedad_antes === undefined ? '' : `${formatearNumero(r.humedad_antes)} % → `}${r.humedad === undefined ? '' : `${formatearNumero(r.humedad)} % · `}${NOMBRE_LETRA[r.letra]}`,
							}
						: r.accion === 'hoja_corregida'
							? // §12.18: lo que cambió, para rehacerla a mano si no entró.
								{ titulo: `Hoja corregida · silo ${r.silo} · precinto ${r.precinto}`, detalle: `${r.cambios?.length ? `${r.cambios.join('; ')} · ` : ''}${NOMBRE_LETRA[r.letra]}` }
							: { titulo: `Muestra · silo ${r.silo} · precinto ${r.precinto}`, detalle: NOMBRE_LETRA[r.letra] };
		// v5 · embolsado (§5.7): el pallet se nombra por su rótulo (r. 22), letra y formato; la tarima, sin número.
		case 'pallet': {
			const que = r.es_tarima ? 'Tarima de espera' : `Pallet ${typeof r.numero === 'number' ? `Nº ${r.numero} · ` : ''}${r.letra ?? ''} · ${(r.formato_bolsa_g / 1000).toFixed(2).replace('.', ',')} kg`;
			const detalle =
				r.accion === 'abrio'
					? 'abierto'
					: r.accion === 'cerro'
						? `cerrado · ${r.bolsas ?? 0} bolsas`
						: r.accion === 'anulo'
							? 'anulado'
							: r.accion === 'unio'
								? // §12.21: de a partes, cuántas pasaron y cuántas quedaron; un recibo de antes solo sabe que pasó.
									r.bolsas === null || r.bolsas === undefined
									? 'pasó a un pallet'
									: `${r.bolsas} bolsas pasaron al pallet${typeof r.destino_numero === 'number' ? ` Nº ${r.destino_numero}` : ''}${r.quedan ? ` · quedan ${r.quedan} en espera` : ''}`
								: 'corregido';
			return { titulo: que, detalle };
		}
		// v6 (§12.2, §12.4)
		case 'aviso':
			return { titulo: r.accion === 'anulo' ? 'Aviso anulado' : 'Aviso para las demás', detalle: r.texto };
		case 'nota_llenado':
			return { titulo: `${r.accion === 'anulo' ? 'Nota anulada' : 'Nota'} · precinto ${r.precinto}`, detalle: r.texto };
		case 'envasado':
			return {
				titulo: `Precinto ${r.precinto} → ${r.destino}`,
				detalle: r.accion === 'anulo' ? 'carga anulada' : `${r.bolsas} bolsas${r.accion === 'corrigio' ? ' (corregido)' : ''}`,
			};
	}
}

/**
 * El motivo, dicho para quien está en la planta. La bandeja mostraba el código
 * técnico del servidor (`silo_ocupado`, `parciales_cambiaron`), que no le dice
 * nada a la encargada a las tres de la mañana: cada uno se traduce a qué pasó y
 * qué se puede hacer. Lo que no está en la tabla se muestra crudo, que es mejor
 * que esconderlo.
 */
const QUE_PASO: Record<string, string> = {
	silo_ocupado: 'ese silo ya tenía otro llenado abierto en esa hora',
	precinto_repetido: 'ese precinto ya se usó en esta campaña',
	tope_silos_abiertos: 'ya había dos silos abiertos: hay que cerrar uno',
	trieur_ocupado: 'otro silo estuvo abierto en ese trieur en ese lapso: un trieur, un silo',
	tara_sobre_parciales: 'la tara corregida no queda por debajo de lo que se pesó en el silo',
	transicion_invalida: 'ya no estaba en ese estado: otro teléfono o la oficina lo cambió antes',
	ya_corregido: 'lo que se vio ya fue corregido por otro teléfono o por la oficina: mirá el inicio y volvé a hacerlo sobre lo de ahora',
	ya_anulado: 'lo que se vio ya fue anulado por otro teléfono o por la oficina',
	id_de_registro_reusado: 'ese identificador ya lo usó otro registro: volvé a cargarlo desde cero',
	seq_reusado: 'la cola de este teléfono se pisó con un envío anterior: avisale al encargado',
	registro_no_encontrado: 'lo que corrige todavía no llegó a la oficina',
	// v6 (§12.2, §12.4, §12.7)
	no_es_el_autor: 'solo lo puede anular el teléfono que lo escribió (o la oficina)',
	registrado_en_futuro: 'la hora del teléfono está adelantada: corregila y volvé a escribirlo',
	arranque_cambiado: 'la temporada se reinició mientras subía: lo de antes se borra',
	payload_invalido: 'el registro llegó mal armado: volvé a cargarlo desde cero',
	silo_cerrado: 'el silo ya estaba cerrado cuando llegó esto',
	parciales_solapados: 'ese turno se pisa con otro ya cargado',
	bruto_no_supera_anterior: 'el peso no supera al del turno anterior',
	rompe_la_cadena: 'el peso no cierra con el turno siguiente',
	inicio_repetido: 'ya hay un turno que empieza a esa hora',
	parciales_cambiaron: 'entró otro turno después del que se vio: revisá el silo',
	cierre_antes_del_ultimo_parcial: 'la hora de cierre es anterior al último turno entregado',
	cierre_anterior_a_apertura: 'la hora de cierre es anterior a la de apertura',
	falta_cerrado_en: 'un silo abierto necesita hora de cierre para descartarse',
	fuera_de_ventana: 'pasó la ventana de corrección: lo arregla la oficina',
	estacion_no_autorizada: 'este teléfono no es de la estación que carga eso',
	campania_cerrada: 'la campaña está cerrada para esa fecha',
	padre_rechazado: 'el registro del que dependía no entró, así que este no viajó',
	id_reusado: 'ese identificador ya se usó para otro registro',
	silo_ya_muestreado: 'ese silo ya tenía una muestra',
	regla_desactualizada: 'la regla de calidad cambió: hay que volver a proponer con la nueva',
	// §12.18: la hoja corregida desde calidad.
	letra_de_envasado: 'la letra la decidió envasado: se respeta, y si hay que cambiarla lo hace la oficina',
	contaminacion_con_bolsas: 'el precinto ya tiene bolsas: una contaminación grave la carga la oficina',
	humedad_cargada: 'la humedad ya estaba cargada: una corrección no la deja vacía',
	sin_regla_de_calidad: 'la oficina todavía no cargó la regla de calidad',
	motivo_requerido: 'la letra cambiada necesita un motivo',
	silo_anulado: 'ese silo fue anulado por la oficina',
	silo_no_llegado: 'el silo todavía no llegó a la oficina',
	// §12.17: corregir una humedad desde el teléfono.
	sin_humedad: 'esa muestra todavía no tenía humedad: completala desde «Falta humedad»',
	contaminacion_grave: 'esa muestra tiene contaminación grave: la humedad la corrige la oficina',
	ya_estaba_asi: 'ya estaba así: no había nada que cambiar',
	tipo_desconocido: 'la oficina todavía no acepta este cambio desde el teléfono: avisale a la oficina y volvé a hacerlo más tarde',
	// v5 · embolsado (§5.7).
	pallet_abierto_repetido: 'ya había un pallet en armado de esa letra y formato: se completa ese',
	composicion_cambio: 'el pallet cambió desde que se vio (entró o salió una carga): mirá el inicio y volvé a cerrarlo',
	pallet_vacio: 'un pallet vacío no se cierra',
	cierre_antes_de_la_carga: 'la hora de cierre es anterior a la última carga del pallet',
	version_de_otro_pallet: 'lo visto no era de ese pallet',
	tarima_sin_letra: 'la tarima todavía no tiene letra: calidad tiene que muestrear el precinto',
	pallet_cerrado: 'el pallet ya estaba cerrado cuando llegó esto',
	pallet_inmovil: 'el pallet ya no admite cambios desde el teléfono',
	// §12.18: corregir o anular una carga desde el teléfono.
	pasaron_48_h: 'pasaron las 48 horas desde que se cargó: lo corrige la oficina',
	pallet_en_mezcla: 'el pallet entró a una mezcla: lo corrige la oficina',
	ultima_carga_del_pallet: 'es la única carga de un pallet cerrado: para sacarla entera, la oficina anula el pallet',
	// 0005 (§12.15): la oficina lo despachó con un remito.
	pallet_despachado: 'el pallet ya salió con un remito: si hay que tocarlo, la oficina lo vuelve a stock',
	pallet_de_otra_letra: 'la letra del precinto no es la del pallet',
	pallet_de_otro_formato: 'el pallet es de otro formato de bolsa',
	campania_cruzada: 'el pallet y el precinto son de campañas distintas',
	precinto_con_letra: 'ese precinto ya tiene letra: va a un pallet, no a una tarima',
	tarima_repetida: 'ese precinto ya tiene su tarima',
	tarima_con_precinto: 'la tarima ya tiene su precinto',
	// §12.21: la tarima que se reparte entre pallets y las cargas de un pase.
	tarima_con_varias_cargas: 'la tarima tiene más de una carga: la reparte la oficina',
	bolsas_de_mas: 'pasaban tantas bolsas como tiene la tarima o más: volvé a mirar la tarima y embolsá de nuevo',
	es_un_pase: 'esas bolsas vinieron de un reparto: la oficina lo deshace y se reparte de nuevo',
	tarima_con_partes: 'la tarima tiene bolsas repartidas en un pallet: primero la oficina deshace el pase',
	precinto_sin_letra: 'ese precinto todavía no tiene letra: no se embolsa',
	precinto_descartado: 'ese silo se descartó: no se embolsa',
	precinto_abierto: 'ese silo sigue abierto: no se embolsa',
	precinto_embolsado: 'ese precinto ya se embolsó: lo corrige la oficina',
	precinto_sobrepasado: `con esa carga se envasan más kilos que los del silo (más de ${BOLSAS_DE_TOLERANCIA} bolsas, o del ${Math.round(TOLERANCIA_SOBRE_NETO * 100)} %, por encima): cargá menos bolsas, o que producción revise el neto del silo`,
	pallet_anulado: 'ese pallet fue anulado por la oficina',
	carga_antes_del_pallet: 'la hora de la carga es anterior a la apertura del pallet',
	carga_despues_del_cierre: 'la hora de la carga es posterior al cierre del pallet',
	pallet_no_llegado: 'el pallet todavía no llegó a la oficina',
	lote_no_es_de_campo: 'ese lote es un batch de blendeo, no un lote de campo',
	batch_no_cerrado: 'ese batch de blendeo todavía no está cerrado',
	// v5, tercera versión: el rótulo emitido (§5.7 r. 22).
	...QUE_PASO_ROTULO,
};

/** El motivo de un rechazo dicho para la planta, con mayúscula: lo usan «lo cargado» y la pantalla de «listo». */
export function queMotivo(motivo: string | null | undefined): string {
	const texto = QUE_PASO[motivo ?? ''] ?? (motivo || 'sin motivo');
	return texto.charAt(0).toUpperCase() + texto.slice(1);
}

const QUE_PASO_FOTO: Record<string, string> = {
	entidad_inexistente: 'el silo no existe en la oficina: la apertura nunca entró o el teléfono se volvió a enrolar',
	foto_resuelta: 'la oficina ya tiene otra foto con ese identificador',
	estacion_no_autorizada: 'este teléfono no es de producción: no puede subir fotos de silos',
	demasiado_grande: 'la foto es demasiado grande para el servidor',
	sin_largo: 'el envío salió sin declarar su tamaño: es un problema de la app, no de la foto',
	entidad_rechazada: 'la apertura del silo no entró (mirá el incidente): la foto no tiene dónde colgarse',
};

/** Una foto que no pudo subir: se reintenta si la causa puede cambiar (403, 411, 413), o se quita. */
function tarjetaFotoFallada(foto: FotoLocal): HTMLElement {
	const caja = el('div', { class: 'ticket incidente' });
	const encabezado = el('div', { class: 'row' });
	encabezado.appendChild(el('span', undefined, `Foto del precinto · ${foto.capturada_en.slice(0, 16).replace('T', ' ')}`));
	encabezado.appendChild(el('b', undefined, foto.falla ? String(foto.falla.codigo || 'sin viajar') : 'retenida'));
	caja.appendChild(encabezado);
	const detalle = el('div', { class: 'row' });
	detalle.appendChild(el('span', undefined, QUE_PASO_FOTO[foto.falla?.motivo ?? ''] ?? (foto.falla?.motivo ?? '')));
	caja.appendChild(detalle);
	// Se reintenta todo salvo el 409: un silo que después sí entró (la apertura se reenvió tras un
	// re-enrolamiento, por ejemplo) recibe la foto; si de verdad no existe, vuelve el 404.
	if (foto.falla && foto.falla.motivo !== 'foto_resuelta') {
		const reintentar = el('button', { class: 'btn go chico ancho', type: 'button' }, 'Volver a intentar');
		reintentar.onclick = async () => {
			await limpiarFallaFoto(app.bd, foto.foto_id);
			app.dispararSync();
			app.render();
		};
		caja.appendChild(reintentar);
	}
	const quitar = botonMantener('Mantener para quitar la foto', 1500, async () => {
		await acusarFotoFallada(app.bd, foto.foto_id);
		app.render();
	});
	quitar.classList.add('chico');
	caja.appendChild(quitar);
	return caja;
}

function tarjetaIncidente(incidente: FilaOutbox): HTMLElement {
	const caja = el('div', { class: 'ticket incidente' });
	const encabezado = el('div', { class: 'row' });
	const visto = describirResumen(incidente.resumen);
	encabezado.appendChild(el('span', undefined, `${incidente.resumen.fecha} · ${visto.titulo}`));
	encabezado.appendChild(el('b', undefined, incidente.estado_local));
	caja.appendChild(encabezado);
	const detalle = el('div', { class: 'row' });
	detalle.appendChild(el('span', undefined, visto.detalle));
	const motivo = incidente.motivo ?? '';
	detalle.appendChild(el('b', undefined, QUE_PASO[motivo] ?? (motivo || 'sin motivo')));
	caja.appendChild(detalle);
	// Una muestra que quedó afuera por la regla no se retipea: vuelve a ser hoja con
	// las mediciones intactas y la letra a decidir con la regla nueva (§5.2.12).
	if (incidente.tipo === 'muestra.crear' && motivo === 'regla_desactualizada' && app.sesion?.estacion === 'calidad') {
		const rehacer = el('button', { class: 'btn go chico ancho', type: 'button' }, 'Volver a proponer con la regla nueva');
		rehacer.onclick = () => void rehacerMuestraRechazada(incidente);
		caja.appendChild(rehacer);
	}
	// Una apertura de pallet que cayó por el rótulo no se retipea: se le pone otro y vuelve a subir
	// con lo que colgaba de ella (§5.7 r. 22).
	if (incidente.tipo === 'pallet.abrir' && MOTIVOS_DE_ROTULO.has(motivo) && app.sesion?.estacion === 'envasado') {
		const rehacer = el('button', { class: 'btn go chico ancho', type: 'button' }, 'Ponerle otro rótulo y volver a mandarlo');
		rehacer.onclick = () => void rehacerAperturaRechazada(incidente);
		caja.appendChild(rehacer);
	}
	const acuse = botonMantener('Mantener para quitar', 1500, async () => {
		await acusarIncidente(app.bd, incidente.id);
		// El rótulo de una apertura que sale de la bandeja deja de estar tomado (§5.7 r. 22).
		if (incidente.tipo === 'pallet.abrir') await recalcularRotulosEnBandeja();
		app.render();
	});
	acuse.classList.add('chico');
	caja.appendChild(acuse);
	return caja;
}

/**
 * Sin estación verde no se captura nada: el servidor rechazaría cada registro
 * como `estacion_no_autorizada` y quedaría en la bandeja. Lo ya encolado sigue
 * subiendo solo; lo nuevo espera a que gestión asigne la estación.
 */
export function pintarSinEstacion(cuerpo: HTMLElement, pie: HTMLElement, vista: Extract<Vista, { tipo: 'sin-estacion' }>): void {
	cuerpo.appendChild(el('div', { class: 'q arriba' }, 'Este teléfono no está asignado a verde'));
	cuerpo.appendChild(
		flag(
			'warn',
			vista.estacion === null ? 'La oficina todavía no le asignó una estación.' : `La oficina lo asignó a «${vista.estacion}».`,
			'Por ahora solo existe la carga de verde. Lo que ya se cargó sigue subiendo; para cargar de nuevo, avisale al encargado.',
		),
	);
	const pendientes = el('button', { class: 'btn ghost ancho', type: 'button' }, 'Ver lo cargado');
	pendientes.onclick = () => acciones.verLoCargado();
	cuerpo.appendChild(pendientes);
	const reintentar = el('button', { class: 'btn go ancho', type: 'button' }, 'Volver a consultar');
	reintentar.onclick = () => {
		app.dispararSync();
		acciones.nuevaCaptura();
	};
	pie.appendChild(reintentar);
}

export function pintarDesenrolado(cuerpo: HTMLElement, pie: HTMLElement): void {
	cuerpo.appendChild(el('div', { class: 'q arriba' }, 'Este teléfono quedó desenrolado'));
	cuerpo.appendChild(
		flag(
			'stop',
			'La oficina dio de baja el acceso de este teléfono.',
			'Los viajes cargados NO se pierden: quedan guardados acá. Avisale al encargado para enrolarlo de nuevo con un código.',
		),
	);
	const boton = el('button', { class: 'btn go ancho', type: 'button' }, 'Enrolar con un código nuevo');
	boton.onclick = () => irA({ tipo: 'enrolar' });
	pie.appendChild(boton);
}

/** ¿La app está abierta como app instalada (sin barra de direcciones)? Fuera de un navegador, no. */
function abiertaComoApp(): boolean {
	try {
		return globalThis.matchMedia?.('(display-mode: standalone)').matches === true || (globalThis.navigator as { standalone?: boolean } | undefined)?.standalone === true;
	} catch {
		return false;
	}
}

/**
 * El cartel de `storage.persist()` negado. La app no sabe si está instalada: solo sabe que el navegador
 * no le aseguró el espacio. Abierta en el navegador, lo más probable es que no esté instalada (Nicolás,
 * 13/9). Abierta como app, Chrome igual puede negarlo —decide por su cuenta—, y decir «no está
 * instalada» era falso (Nicolás, 2/10). En los dos casos se dice qué se pierde.
 */
export function avisoDePersistencia(persistenciaOk: boolean | undefined, comoApp: boolean): [string, string] | null {
	if (persistenciaOk !== false) return null;
	if (comoApp) {
		return ['El teléfono no le aseguró espacio permanente a la app.', 'Lo cargado sube igual con señal; si se llena la memoria antes de subir, podría perderse.'];
	}
	return ['La app no está instalada en este teléfono.', 'Lo cargado sube igual, pero si queda sin señal el teléfono podría borrarlo. Que el encargado la instale en la pantalla de inicio.'];
}
